import { useEffect, useState } from 'react';
import type { AxiosResponse } from 'axios';
import type { AccentColor } from '@udbodhanam/shared';
import apiClient from '../services/apiClient';
import { useAuthStore } from '../stores/useAuthStore';
import { useLanguageStore } from '../stores/useLanguageStore';
import i18n from '../i18n/config';
import { applyAccentColor } from '../lib/accentColor';

interface MeResponse {
  data: {
    id: string;
    username: string;
    fullName: string;
    role: string;
    languagePreference: string;
    consentStatus: string;
    mustChangePassword: boolean;
    accountType: string;
    paymentAccessReason: 'NEVER_PAID' | 'EXPIRED' | null;
  };
}

// Public routes that require no auth check — skipping /users/me here prevents
// the Axios refresh interceptor from triggering a hard-reload loop when the
// user has no session cookies (e.g. first visit or after logout).
const PUBLIC_PATHS = ['/login', '/forgot-password', '/reset-password', '/guardian-consent', '/aspirant/signup'];

// The API may not be accepting connections yet when the SPA boots — in dev the
// Nest watcher respawns and takes ~10s to bind :3000, in prod a container roll
// does the same. A network-level failure says nothing about whether the session
// is valid, so retry with backoff (~15s) before giving up. Only a real HTTP 4xx
// means "not logged in"; that still fails fast and redirects immediately.
const RETRY_DELAYS_MS = [1000, 2000, 4000, 8000];

function isTransient(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return status == null || status >= 500;
}

async function getWithRetry<T>(url: string): Promise<AxiosResponse<T>> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await apiClient.get<T>(url);
    } catch (error) {
      if (attempt >= RETRY_DELAYS_MS.length || !isTransient(error)) throw error;
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS_MS[attempt]));
    }
  }
}

export default function AuthInitializer({ children }: { children: React.ReactNode }) {
  const [booting, setBooting] = useState(true);
  const setAuth = useAuthStore((s) => s.setAuth);

  useEffect(() => {
    getWithRetry<{ data: { accentColor: AccentColor } }>('/settings')
      .then((res) => applyAccentColor(res.data.data.accentColor))
      .catch(() => { /* best-effort; localStorage cache stays */ });

    if (PUBLIC_PATHS.some((p) => window.location.pathname.startsWith(p))) {
      setBooting(false);
      return;
    }

    getWithRetry<MeResponse>('/users/me')
      .then(async (res) => {
        const u = res.data.data;
        setAuth(u, u.role, u.consentStatus, u.mustChangePassword ?? false, false);
        // DB preference is authoritative — it reflects the user's last saved choice
        // across all devices. Overwrite any stale sessionStorage so this browser
        // converges to the correct language on rehydration.
        const apiLang = (u.languagePreference ?? 'EN').toLowerCase();
        sessionStorage.setItem('appLang', apiLang);
        useLanguageStore.getState().setLanguage(apiLang);
        await i18n.changeLanguage(apiLang);
      })
      .catch(() => {
        // Reached only on a real 4xx (no/expired session) or after the retries
        // above are exhausted. leave isAuthenticated: false — ProtectedRoute
        // will redirect to /login.
      })
      .finally(() => setBooting(false));
  }, [setAuth]);

  if (booting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface">
        <span className="h-8 w-8 border-4 border-primary-300 border-t-primary-600 rounded-full animate-spin" />
      </div>
    );
  }

  return <>{children}</>;
}
